import { useEffect, useState } from "react";
import { Container, Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import StoreProductGallery from "../components/StoreProductDetail/StoreProductGallery";
import StoreProductPurchasePanel from "../components/StoreProductDetail/StoreProductPurchasePanel";
import StoreProductDescription from "../components/StoreProductDetail/StoreProductDescription";

export default function StoreProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const controller = new AbortController();

    const loadProduct = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`/api/store/public/products/${slug}/`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        const contentType = res.headers.get("content-type") || "";

        if (!contentType.includes("application/json")) {
          throw new Error("La respuesta no es JSON");
        }

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error || "No se pudo cargar el producto");
        }

        setProduct(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        setProduct(null);
        setError(err.message || "Error al cargar el producto");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => controller.abort();
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ backgroundColor: "#f3f4f6", py: { xs: 2, md: 3 } }}>
        <Container maxWidth={false} sx={{ maxWidth: "1480px", px: { xs: 1.5, sm: 2, md: 4, xl: 6 } }}>
          <Box
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "28px",
              minHeight: 420,
            }}
          />
          <Box
            sx={{
              mt: 2.4,
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "28px",
              minHeight: 220,
            }}
          />
        </Container>
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ backgroundColor: "#f3f4f6", py: { xs: 2, md: 3 } }}>
        <Container maxWidth={false} sx={{ maxWidth: "1480px", px: { xs: 1.5, sm: 2, md: 4, xl: 6 } }}>
          <Box
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "28px",
              p: { xs: 2, md: 3 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 20, md: 28 },
                fontWeight: 800,
                color: "#111827",
              }}
            >
              {error || "No se encontró el producto"}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f3f4f6", py: { xs: 2, md: 3 } }}>
      <Container maxWidth={false} sx={{ maxWidth: "1480px", px: { xs: 1.5, sm: 2, md: 4, xl: 6 } }}>
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "28px",
            px: { xs: 1.2, sm: 1.6, md: 2.4, lg: 3 },
            py: { xs: 1.2, sm: 1.6, md: 2.4, lg: 3 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr) minmax(0, 1fr)", md: "1.08fr 0.92fr" },
              gap: { xs: 1.2, sm: 1.6, md: 2.2, lg: 3 },
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <StoreProductGallery product={product} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <StoreProductPurchasePanel product={product} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2.4 }}>
          <StoreProductDescription description={product?.description || ""} />
        </Box>
      </Container>
    </Box>
  );
}